import { useMemo, useRef, useEffect } from 'react';
import {
  HighlightTextarea,
  Highlight,
} from '@antimatterhq/ui/components/highlightTextarea';
import { Tabs } from '@antimatterhq/ui/components/tabs';
import { Text } from '@antimatterhq/ui/components/typography';
export interface DiffTextareaProps {
  current: string;
  previous: string;
  onCurrentChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onPreviousChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  previousSource: 'upgradePreset' | 'currentPreset' | 'currentValue';
  onChangePreviousSource: (
    source: 'upgradePreset' | 'currentPreset' | 'currentValue',
  ) => void;
}

export const DiffTextarea = ({
  current,
  previous,
  onCurrentChange,
  onPreviousChange,
  previousSource,
  onChangePreviousSource,
}: DiffTextareaProps) => {
  const currentRef = useRef<HTMLTextAreaElement>(null);
  const previousRef = useRef<HTMLTextAreaElement>(null);
  const isScrolling = useRef(false);

  // This generates the highlights and aligned text for the current and previous textareas
  const [currentHighlights, previousHighlights, currentText, previousText] =
    useMemo(() => {
      const curHighlights: Highlight[] = [];
      const prevHighlights: Highlight[] = [];

      const curLines = current.split('\n');
      const prevLines = previous.split('\n');

      // Create aligned arrays of lines with empty lines inserted for additions/deletions
      const alignedCurrent: string[] = [];
      const alignedPrevious: string[] = [];

      let i = 0;
      let j = 0;

      while (i < curLines.length || j < prevLines.length) {
        const curLine = curLines[i];
        const prevLine = prevLines[j];

        if (i >= curLines.length) {
          // Remaining lines in previous, add empty lines to current
          alignedCurrent.push('');
          alignedPrevious.push(prevLine);
          j++;
        } else if (j >= prevLines.length) {
          // Remaining lines in current, add empty lines to previous
          alignedCurrent.push(curLine);
          alignedPrevious.push('');
          i++;
        } else if (curLine === prevLine) {
          // Lines match, add to both
          alignedCurrent.push(curLine);
          alignedPrevious.push(prevLine);
          i++;
          j++;
        } else {
          // Lines differ, need to look ahead to find next match
          const curNext = curLines.slice(i + 1);
          const prevNext = prevLines.slice(j + 1);

          const nextMatchInCur = prevLine ? curNext.indexOf(prevLine) : -1;
          const nextMatchInPrev = curLine ? prevNext.indexOf(curLine) : -1;

          if (
            nextMatchInCur >= 0 &&
            (nextMatchInPrev === -1 || nextMatchInCur <= nextMatchInPrev)
          ) {
            // Current has extra lines before next match
            alignedCurrent.push(curLine);
            alignedPrevious.push('');
            i++;
          } else if (nextMatchInPrev >= 0) {
            // Previous has extra lines before next match
            alignedCurrent.push('');
            alignedPrevious.push(prevLine);
            j++;
          } else {
            // No future matches, treat as modified line
            alignedCurrent.push(curLine);
            alignedPrevious.push(prevLine);
            i++;
            j++;
          }
        }
      }

      // Convert back to text and calculate highlights
      const alignedCurrentText = alignedCurrent.join('\n');
      const alignedPreviousText = alignedPrevious.join('\n');

      // Calculate highlights based on aligned text
      let curPos = 0;
      let prevPos = 0;

      for (let k = 0; k < alignedCurrent.length; k++) {
        const curLine = alignedCurrent[k];
        const prevLine = alignedPrevious[k];

        if (curLine !== prevLine) {
          if (curLine) {
            curHighlights.push({
              start: curPos,
              end: curPos + curLine.length,
              variant: 'success',
            });
          }
          if (prevLine) {
            prevHighlights.push({
              start: prevPos,
              end: prevPos + prevLine.length,
              variant: 'error',
            });
          }
        }

        curPos += curLine.length + 1; // +1 for newline
        prevPos += prevLine.length + 1;
      }

      return [
        curHighlights,
        prevHighlights,
        alignedCurrentText,
        alignedPreviousText,
      ];
    }, [current, previous]);

  useEffect(() => {
    const currentRefCurrent = currentRef.current;
    const previousRefCurrent = previousRef.current;

    if (!currentRefCurrent || !previousRefCurrent) return;

    const handleScroll = (
      source: HTMLTextAreaElement,
      target: HTMLTextAreaElement,
    ) => {
      if (!isScrolling.current) {
        isScrolling.current = true;
        // eslint-disable-next-line no-param-reassign
        target.scrollTop = source.scrollTop;
        requestAnimationFrame(() => {
          isScrolling.current = false;
        });
      }
    };

    const handleCurrentScroll = () => {
      if (currentRefCurrent && previousRefCurrent) {
        handleScroll(currentRefCurrent, previousRefCurrent);
      }
    };

    const handlePreviousScroll = () => {
      if (previousRefCurrent && currentRefCurrent) {
        handleScroll(previousRefCurrent, currentRefCurrent);
      }
    };

    currentRefCurrent.addEventListener('scroll', handleCurrentScroll);
    previousRefCurrent.addEventListener('scroll', handlePreviousScroll);

    return () => {
      currentRefCurrent.removeEventListener('scroll', handleCurrentScroll);
      previousRefCurrent.removeEventListener('scroll', handlePreviousScroll);
    };
  }, []);

  return (
    <div
      className="flex w-full flex-row gap-2"
      data-testid="diff-textarea-root"
    >
      <Tabs.Root>
        <Tabs.List>
          <Tabs.Tab
            isSelected={previousSource === 'currentPreset'}
            onSelect={() => onChangePreviousSource('currentPreset')}
            className="flex-1"
            data-testid="diff-textarea-tab-currentPreset"
          >
            Original version
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={previousSource === 'currentValue'}
            onSelect={() => onChangePreviousSource('currentValue')}
            className="flex-1"
            data-testid="diff-textarea-tab-currentValue"
          >
            Your edited version
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={previousSource === 'upgradePreset'}
            onSelect={() => onChangePreviousSource('upgradePreset')}
            className="flex-1"
            data-testid="diff-textarea-tab-upgradePreset"
          >
            Latest version
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel isSelected={true} className="bg-white dark:bg-[#11171C]">
          <HighlightTextarea
            value={previousText}
            highlights={previousHighlights}
            height="160px"
            onChange={onPreviousChange}
            ref={previousRef}
            disabled
            data-testid="diff-textarea-previous"
          />
        </Tabs.Panel>
      </Tabs.Root>
      <div className="flex w-full bg-white dark:bg-[#11171C]">
        <div className="w-full px-4 pb-4 pt-[9px]">
          <Text
            className="mb-6 block"
            data-testid="diff-textarea-pending-label"
          >
            Pending changes
          </Text>
          <HighlightTextarea
            value={currentText}
            highlights={currentHighlights}
            height="160px"
            onChange={onCurrentChange}
            ref={currentRef}
            data-testid="diff-textarea-current"
          />
        </div>
      </div>
    </div>
  );
};
